import { ReactElement } from 'react';
import Carousel from 'components/UI/Carousel';
import Link from 'components/UI/Link';
import {
  IComponentArticleCarouselFields,
  IComponentHeroInlineCircle,
  ITemplateCareersPageFields,
  ITemplateContactUsFields,
  ITemplateEcpAboutUsFields,
  ITemplateEcpAcquisitionPageFields,
  ITemplateEcpCaresPageFields,
  ITemplateEcpHomePageFields,
  ITemplateEcpOurPracticesPageFields,
  ITemplateEcpProspectPageFields,
  ITemplateFreeformPageFields,
  ITemplateHomePageFields,
  ITemplateHubSiteCareersPageFields,
  ITemplateHubSiteHomePageFields,
  ITemplateHubSiteIndividualServicesFields,
  ITemplateHubSiteOurServicesFields,
  ITemplateHubSitePromosPageFields,
} from 'types/contentful';
import { Entry } from 'contentful';

// Define a more explicit ContentEntry type to handle `fields`
type ContentEntry = Entry<{
  image?: {
    fields: {
      file: {
        url: string;
      };
    };
  };
}>;

type TemplateFields = Partial<
  | ITemplateEcpAboutUsFields
  | ITemplateEcpAcquisitionPageFields
  | ITemplateCareersPageFields
  | ITemplateEcpCaresPageFields
  | ITemplateContactUsFields
  | ITemplateEcpHomePageFields
  | ITemplateEcpOurPracticesPageFields
  | ITemplateEcpProspectPageFields
  | ITemplateFreeformPageFields
  | ITemplateHomePageFields
  | ITemplateHubSiteCareersPageFields
  | ITemplateHubSiteHomePageFields
  | ITemplateHubSiteIndividualServicesFields
  | ITemplateHubSiteOurServicesFields
  | ITemplateHubSitePromosPageFields
>;

export default function ArticleCarousel({
  heading,
  blocks,
}: IComponentArticleCarouselFields): ReactElement {
  const aspectRatio = 'landscape';

  // `hasHero` is used to check if `hero` exists, and optionally `content`
  const hasHero = (
    fields: TemplateFields
  ): fields is { hero: IComponentHeroInlineCircle[]; content?: ContentEntry[] } => {
    if ('hero' in fields && Array.isArray(fields.hero)) {
      return true;
    }

    if ('content' in fields && fields.content) {
      return fields.content.some((entry) => entry?.fields?.image);
    }

    return false;
  };

  const carouselItems = blocks.map(({ fields: { slug, title, template } }, index) => {
    let image = '/images/preview-placeholder.jpeg';

    if (template?.fields && hasHero(template.fields)) {
      image =
        template.fields.hero?.[0]?.fields.image?.fields.file?.url ||
        (Array.isArray(template.fields.content) &&
          template.fields.content[0]?.fields?.image?.fields.file?.url) ||
        '/images/preview-placeholder.jpeg';
    }

    return (
      <div
        className="flex h-full flex-col bg-neutral text-left max-w-xs mx-auto md:max-w-full"
        key={index}
      >
        {image && (
          <div
            style={{ backgroundImage: `url('${image}')`, aspectRatio }}
            className={`aspect-ratio-${aspectRatio} bg-cover bg-center`}
          ></div>
        )}
        <div className="flex flex-1 flex-col px-4 py-4 md:px-5 md:py-5">
          {title && <div className="text-2xl leading-tight text-primary-actual">{title}</div>}
          <div className="mt-auto pt-4">
            <Link slug={`/${slug}`}>
              <a className="inline-flex items-center gap-2 text-tertiary-actual text-lg">
                <span>Learn More</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="4"
                  height="8"
                  viewBox="0 0 9 15"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M1.33301 1.33301L7.33301 7.33301L1.33301 13.333"
                    stroke="currentColor"
                    strokeWidth="2.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </Link>
          </div>
        </div>
      </div>
    );
  });

  return (
    <Carousel
      title={heading}
      items={carouselItems}
      className="carousel-articles ecp-my pb-10 md:pb-20 max-w-85% mx-auto"
      contentClassName="w-full"
      titleClassName="mb-16"
    />
  );
}
